import React, {
  FC,
  useRef,
  useState,
  useEffect,
  useMemo,
  Fragment,
  Dispatch,
  SetStateAction,
  RefObject,
} from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Input,
  Pressable,
  Button,
  Image,
  ScrollView,
} from "native-base";
import { Animated } from "react-native";
import * as AV from "../../AVCore/AVCore";
import { SignalRService } from "../../AVCore/SignalR";
import useLiveSignalR from "../../Hooks/useLiveSignalR";
import Tooltip from "@mui/material/Tooltip";
import {
  AddIcon,
  LiveIcon,
  LiveMinIcon,
  LiveMaxIcon,
  PaperPlaneIcon,
  DollarCoinsIcon,
  PreLivestreamIcon,
} from "../../assets/icons/icon-assets";
import inform from "../../assets/images/inform.png";
import { FiVolume2, FiVolumeX, FiUsers, FiEye, FiEyeOff } from "react-icons/fi";
import AVCancel from "./AVCancel";
import { useWindowDimension } from "../../Utility/WindowDimensions";
import { Loc } from "../../App/Loc";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import {
  setUser,
  setEntitydata,
  setPostPopupExpanded,
} from "../../Redux/Actions/userActions";
import { setViewLiveMinimized } from "../../Redux/Actions/liveStreamActions";
import settings from "../../Platform/settings.json";
import CustomToast from "../../Hooks/useCustomToast";
import EntityProfilePic from "../../components/common/EntityProfilePic";
import PurchaseCreditsPopup from "../../components/common/PurchaseCreditsPopup";
import AVConfirmationModal from "../../components/common/AVConfirmationModal";
import AgoraRTC from "agora-rtc-sdk-ng";
import { removeLiveStream } from "../../Redux/Actions/liveStreamActions";

/* -------------------- AGORA CONFIG -------------------- */
const AGORA_APP_ID = "4ec5b1ce4f0a40f08c7fb81c6ec10e60";
const WATCH_COST_PER_MIN = 25; // credits
const WATCH_START_CREDITS = 125; // 5 min

interface AVLiveViewPopupProps {
  onClose: () => void;
  showLiveViewer?: AV.ILiveStream | null;
}

type VideoUser = {
  uid: string;
  videoTrack: any;
  hasVideo?: boolean;
  hasAudio?: boolean;
  isLocal?: boolean;
  isSpeaking?: boolean;
  isPortrait?: boolean;
  isHost?: boolean;
  networkQuality?: number;
};

/* -------------------- OUTSIDE CLICK HOOK -------------------- */
function useOutsideAlerter(ref: RefObject<any>, callback: () => void) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
}

const AVLiveViewPopup: FC<AVLiveViewPopupProps> = ({
  onClose,
  showLiveViewer,
}) => {
  const [open, setOpen] = useState(true);

  const dimensions = useWindowDimension();
  const mobile = dimensions?.width < 600;
  const dispatch = useDispatch();

  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user[0],
  );
  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );
  const activeStreams = useSelector(
    (state: RootStateOrAny) => state.liveStream.activeStreams,
  );
  const isMinimized = useSelector(
    (state: RootStateOrAny) => state.liveStream.isViewLiveMinimized,
  );

  // WATCH LIVE STATE
  const [isWatchLive, setIsWatchLive] = useState(false);

  const [isAPICall, setIsAPICall] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const [entity, setEntity] = useState<AV.IAVEntity>(currentUser);
  const [sharedPlan, setSharedPlan] = useState<AV.ISharedPlan>();
  const [showCreditsPopup, setShowCreditsPopup] = useState(false);
  const [creditsAvailable, setCreditsAvailable] = useState<number>(0);
  const [showLowCreditPopup, setShowLowCreditPopup] = useState(false);
  const [showMoreCreditPopup, setShowMoreCreditPopup] = useState(false);
  const [eventParentEntity, setEventParentEntity] = useState<AV.IAVEntity>();
  const [finalEntity, setFinalEntity] = useState<AV.IAVEntity | undefined>(
    currentUser,
  );
  const [liveStream, setLiveStream] = useState<AV.ILiveStream | null>(null);
  const [showOnlyVideo, setShowOnlyVideo] = useState(false);

  /* ===================== POPUP STYLES ===================== */
  const popupOverlay = {
    position: "fixed" as const,
    bg: "rgba(0,0,0,0.5)",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 999999,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    display: "flex" as const,
  };

  const popupBox = {
    bg: "white",
    p: 6,
    borderRadius: "12px",
    width: "340px",
    shadow: "9",
  };

  /* ===================== TEXT FORMAT HELPER ===================== */
  // Replace placeholders like {name} in strings
  const formatText = (text: string, values: any[]) => {
    let index = 0;

    return text
      .replace(/\{\s*(.*?)\s*\}/g, () => {
        return values[index++] ?? "";
      })
      .replace(/\s+([.,!?])/g, "$1")
      .replace(/\b(\w+)\s+s\b/g, "$1s");
  };

  //#region Livestream Section

  /* ===================== AGORA CLIENT REF ===================== */
  const clientRef = useRef<any>(null);

  /* ===================== VIDEO USERS STATE ===================== */
  // Stores all video participants (host + co-hosts)
  const [videoUsers, setVideoUsers] = useState<VideoUser[]>([]);

  /* ===================== SPEAKER CONTROL ===================== */
  // Controls whether remote audio is ON/OFF
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);

  /* ===================== REMOTE AUDIO TRACKS ===================== */
  // Store all remote users' audio tracks
  // Key = userId, Value = audioTrack
  const remoteAudioTracksRef = useRef<Map<string, any>>(new Map());

  /* ===================== TOGGLE SPEAKER ===================== */
  const toggleSpeaker = () => {
    if (isSpeakerOn) {
      /* ---------- MUTE ALL REMOTE AUDIO ---------- */
      remoteAudioTracksRef.current.forEach((track) => {
        try {
          track.stop(); // Stops audio playback
        } catch {}
      });

      setIsSpeakerOn(false);
    } else {
      /* ---------- UNMUTE ALL REMOTE AUDIO ---------- */
      remoteAudioTracksRef.current.forEach((track) => {
        try {
          track.play(); // Resume audio playback
        } catch {}
      });

      setIsSpeakerOn(true);
    }
  };

  /* ===================== ADD USER ===================== */
  const addUser = (uid: string | number, videoTrack: any) => {
    const id = String(uid);

    setVideoUsers((prev) => {
      const exists = prev.find((u) => u.uid === id);
      if (exists) return prev;

      return [
        ...prev,
        {
          uid: id,
          videoTrack,
          hasVideo: false,
          hasAudio: false,
          isPortrait: false,
        },
      ];
    });
  };

  /* ===================== REMOVE USER ===================== */
  const removeUser = (uid: string | number) => {
    const id = String(uid);
    setVideoUsers((prev) => prev.filter((u) => u.uid !== id));
  };

  /* ===================== GRID CALCULATION ===================== */
  const getColumns = (count: number) => {
    if (count <= 1) return 1;
    if (count === 2) return 2;
    if (count <= 4) return 2;
    if (count <= 6) return 3;
    if (count <= 9) return 3;
    return 4;
  };

  // Calculate layout
  const columns = getColumns(videoUsers.length);
  const itemWidth = `${100 / columns}%`;

  /* ===================== NETWORK LABEL ===================== */
  const getNetworkLabel = (q?: number) => {
    switch (q) {
      case 1:
        return "Excellent";
      case 2:
        return "Good";
      case 3:
        return "Poor";
      case 4:
        return "Bad";
      case 5:
        return "Very Bad";
      case 6:
        return "Disconnected";
      default:
        return "";
    }
  };

  /* ===================== NETWORK COLOR ===================== */
  const getColor = (q?: number) => {
    if (!q) return "gray.400";

    if (q <= 2) return "green.400";
    if (q <= 4) return "yellow.400";
    return "red.400";
  };

  /* ===================== JOIN LIVE (WATCHER) ===================== */
  const joinLive = async () => {
    try {
      /* ---------- CHECK CREDITS ---------- */
      if (creditsAvailable < WATCH_START_CREDITS) {
        setShowMoreCreditPopup(true);
        return;
      }

      setIsAPICall(true);

      /* ---------- CREATE AGORA CLIENT ---------- */
      const client = AgoraRTC.createClient({
        mode: "live",
        codec: "vp8",
      });

      clientRef.current = client;

      // Enable speaking detection
      client.enableAudioVolumeIndicator();

      // Set role as audience (viewer)
      client.setClientRole("audience");

      /* ---------------- USER PUBLISHED ---------------- */
      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);

        if (showLiveViewer?.host) {
          setJoinUsers((prev: AV.ILink[]) => {
            const exists = prev.some(
              (item) => item?.id === showLiveViewer?.host?.id,
            );
            return exists ? prev : [...prev, showLiveViewer?.host];
          });
        }

        if (currentUser) {
          setJoinUsers((prev: AV.ILink[]) => {
            const exists = prev.some((item) => item?.id === currentUser?.id);
            return exists ? prev : [...prev, AV.AVEntity.getLink(currentUser)];
          });
        }

        setTimeout(() => {
          let isPortrait = false;
          if (mediaType === "video" && user.videoTrack) {
            const settings = user.videoTrack
              .getMediaStreamTrack()
              .getSettings();
            isPortrait = (settings?.height ?? 0) > (settings?.width ?? 0);
            console.log("isPortrait", isPortrait);
          }

          const id = String(user.uid);

          /* ---------- UPDATE VIDEO USERS LIST ---------- */
          setVideoUsers((prev) => {
            const exists = prev.find((u) => u.uid === id);

            // If user not exists → add
            if (!exists) {
              return [
                ...prev,
                {
                  uid: id,
                  videoTrack: user.videoTrack,
                  hasVideo: mediaType === "video",
                  hasAudio: mediaType === "audio",
                  isPortrait: isPortrait,
                  isSpeaking: false,
                },
              ];
            }

            // If exists → update properties
            return prev.map((u) => {
              if (u.uid !== id) return u;

              return {
                ...u,
                videoTrack: user.videoTrack || u.videoTrack,
                hasVideo: mediaType === "video" ? true : u.hasVideo,
                hasAudio: mediaType === "audio" ? true : u.hasAudio,
                isPortrait: mediaType === "video" ? isPortrait : u.isPortrait,
              };
            });
          });

          /* ---------- HANDLE AUDIO ---------- */
          if (mediaType === "audio") {
            user.audioTrack?.play();

            // Store audio track for speaker toggle
            remoteAudioTracksRef.current.set(String(user.uid), user.audioTrack);
          }
        }, 3000);
      });

      /* ---------------- USER UNPUBLISHED ---------------- */
      client.on("user-unpublished", (user, mediaType) => {
        const id = String(user.uid);

        // Remove audio track
        if (mediaType === "audio") {
          remoteAudioTracksRef.current.delete(String(user.uid));
        }

        // Update user state
        setVideoUsers((prev) =>
          prev.map((u) => {
            if (u.uid !== id) return u;

            return {
              ...u,
              hasVideo: mediaType === "video" ? false : u.hasVideo,
              hasAudio: mediaType === "audio" ? false : u.hasAudio,
              isPortrait: mediaType === "video" ? false : u.isPortrait,
            };
          }),
        );
      });

      /* ---------------- USER LEFT ---------------- */
      client.on("user-left", (user) => {
        // Remove audio track
        remoteAudioTracksRef.current.delete(String(user.uid));

        // Remove from UI
        removeUser(user.uid);
      });

      /* ---------------- SPEAKING INDICATOR ---------------- */
      client.on("volume-indicator", (volumes) => {
        setVideoUsers((prev) =>
          prev.map((user) => {
            const match = volumes.find((v) => String(v.uid) === user.uid);

            return {
              ...user,
              isSpeaking: match ? match.level > 5 : false,
            };
          }),
        );
      });

      /* ---------------- NETWORK QUALITY ---------------- */
      client.on("network-quality", (stats) => {
        setVideoUsers((prev) =>
          prev.map((user) => {
            if (user.isLocal) {
              return {
                ...user,
                networkQuality: stats.uplinkNetworkQuality,
              };
            }

            return {
              ...user,
              networkQuality: stats.downlinkNetworkQuality,
            };
          }),
        );
      });

      /* ---------------- JOIN SIGNALR GROUP ---------------- */
      await SignalRService.joinLiveChat(showLiveViewer?.channelId ?? "");
      setIsWatchLive(true);

      /* ---------------- JOIN AGORA CHANNEL ---------------- */
      await client.join(
        AGORA_APP_ID,
        showLiveViewer?.channelId ?? "",
        showLiveViewer?.token ?? "",
        null,
      );

      /* ---------- UPDATE STATE ---------- */
      setLiveStream(showLiveViewer ?? null);

      // Start credit usage timer
      startChargeTimer();

      setIsAPICall(false);
    } catch (err) {
      setIsAPICall(false);
      setIsWatchLive(false);
      console.error(err);
    }
  };

  /* ===================== LEAVE LIVE (WATCHER) ===================== */
  const leaveLive = async () => {
    try {
      setIsAPICall(true);
      /* ---------- STOP TIMER ---------- */
      stopChargeTimer();

      /* ---------- CLOSE SESSION ---------- */
      if (liveStream) {
        const target = finalEntity;
        if (!target) return;

        await AV.Media.closeLiveSession({
          target: AV.AVEntity.getFk(target),
          minutes: accumulatedMinutes.current,
          mode: "Watch",
          channelId: showLiveViewer?.channelId ?? "",
          resourceId: showLiveViewer?.resourceId ?? "",
          sid: showLiveViewer?.sid ?? "",
        });
      }

      /* ---------- LEAVE SIGNALR GROUP ---------- */
      if (sessionIdRef.current) {
        await SignalRService.leaveLiveChat(sessionIdRef.current);
      }

      /* ---------- LEAVE AGORA ---------- */
      await clientRef.current?.leave();
      clientRef.current = null;

      /* ---------- RESET UI ---------- */
      setLiveStream(null);

      onClose();
      dispatch(setPostPopupExpanded(false));
      dispatch(setViewLiveMinimized(false));
      setIsAPICall(false);
    } catch (e) {
      setIsAPICall(false);
      console.warn("Leave live error", e);
    }
  };

  /* ===================== CREDIT TIMER ===================== */
  const chargeTimer = useRef<any>(null);
  const accumulatedMinutes = useRef<number>(0);
  const sessionIdRef = useRef<string | null>(null);
  const entityRef = useRef(finalEntity);
  const entityDataListRef = useRef(entityDataList);

  /* ---------- KEEP LATEST STATE ---------- */
  useEffect(() => {
    entityRef.current = finalEntity;
  }, [entity]);

  useEffect(() => {
    entityDataListRef.current = entityDataList;
  }, [entityDataList]);

  /* ---------- SAFE PLAN BUILDER ---------- */
  const safePlan = (plan: any, credits: number) => ({
    storeLimit: plan?.storeLimit ?? 0,
    baseStoreLimit: plan?.baseStoreLimit ?? 0,
    payStoreEndDate: plan?.payStoreEndDate ?? "",
    topEntities: plan?.topEntities ?? 0,
    storeUsed: plan?.storeUsed ?? 0,
    shared: plan?.shared,
    credits,
  });

  /* ---------- UPDATE ENTITY LIST ---------- */
  const updateEntityCredits = (
    list: any[],
    entityId: string,
    newCredits: number,
  ) => {
    if (!entityId) return list;

    return list.map((item) => {
      if (item?.id === entityId) {
        return {
          ...item,
          plan: safePlan(item.plan, newCredits),
        };
      }
      return item;
    });
  };

  /* ---------- START TIMER ---------- */
  const startChargeTimer = () => {
    if (chargeTimer.current) return;

    chargeTimer.current = setInterval(async () => {
      try {
        accumulatedMinutes.current += 1;

        const currentEntity = entityRef.current;
        const currentList = entityDataListRef.current;
        if (!currentEntity) return;

        const oldCredits = currentEntity?.plan?.credits ?? 0;

        const credits = await AV.Media.chargeLiveCredits({
          target: AV.AVEntity.getFk(currentEntity),
          minutes: 1,
          mode: "Watch",
          channelId: "",
          resourceId: "",
          sid: "",
        });

        console.log("OLD:", oldCredits, "NEW:", credits);

        /* -------------------- UI -------------------- */
        setCreditsAvailable(credits);

        let updatedList = [...currentList];

        /* -------------------- OWN OR SHARED -------------------- */
        const ownUsed = credits !== oldCredits;

        if (currentEntity?.type !== "Event" && currentEntity?.type !== "Game") {
          if (ownUsed) {
            updatedList = updateEntityCredits(
              updatedList,
              currentEntity?.id ?? "",
              credits,
            );

            setEntity((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );
          } else if (currentEntity?.plan?.shared?.id) {
            updatedList = updateEntityCredits(
              updatedList,
              currentEntity.plan.shared.id,
              credits,
            );

            setSharedPlan((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );
          }

          setFinalEntity((prev) =>
            prev
              ? {
                  ...prev,
                  plan: safePlan(prev.plan, credits),
                }
              : prev,
          );
        } else {
          /* ================= EVENT ================= */
          const parent = currentList.find(
            (x) => x?.id === currentEntity?.parent?.id,
          );

          if (!parent) return;

          if (ownUsed) {
            updatedList = updateEntityCredits(updatedList, parent.id, credits);

            setEventParentEntity((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );
          } else if (parent?.plan?.shared?.id) {
            updatedList = updateEntityCredits(
              updatedList,
              parent.plan.shared.id,
              credits,
            );

            setSharedPlan((prev) =>
              prev
                ? {
                    ...prev,
                    plan: safePlan(prev.plan, credits),
                  }
                : prev,
            );
          }

          setEntity((prev) =>
            prev
              ? {
                  ...prev,
                  plan: safePlan(prev.plan, credits),
                }
              : prev,
          );

          setFinalEntity((prev) =>
            prev
              ? {
                  ...prev,
                  plan: safePlan(prev.plan, credits),
                }
              : prev,
          );
        }

        /* -------------------- REDUX -------------------- */
        dispatch(setEntitydata(updatedList));

        /* -------------------- CURRENT USER -------------------- */
        if (currentEntity?.id === currentUser?.id && ownUsed) {
          dispatch(
            setUser({
              ...currentUser,
              plan: safePlan(currentUser?.plan, credits),
            }),
          );
        }

        /* -------------------- AUTO STOP WHEN NO CREDITS -------------------- */
        if (credits < WATCH_COST_PER_MIN) {
          const clientError = {
            message: Loc.currentLang.liveStream.watchEnded,
          };
          CustomToast({ clientError });

          await leaveLive();
          setShowCreditsPopup(true);
        }
      } catch (err) {
        console.error("Charge API error", err);
      }
    }, 60000);
  };

  /* ---------- STOP TIMER ---------- */
  const stopChargeTimer = () => {
    if (chargeTimer.current) {
      clearInterval(chargeTimer.current);
      chargeTimer.current = null;
    }
  };

  /* ---------- CLEANUP ---------- */
  useEffect(() => {
    return () => {
      stopChargeTimer();
    };
  }, []);

  /* ===================== LOW CREDIT WARNING ===================== */
  useEffect(() => {
    if (
      creditsAvailable <= WATCH_START_CREDITS &&
      creditsAvailable > 0 &&
      liveStream
    ) {
      setShowLowCreditPopup(true);
    }
  }, [creditsAvailable]);

  /* ===================== AUTO STOP WHEN NO CREDITS ===================== */
  //useEffect(() => {
  //  const handleStop = async () => {
  //    if (creditsAvailable <= WATCH_COST_PER_MIN && liveStream) {
  //      const clientError = {
  //        message: Loc.currentLang.liveStream.watchEnded,
  //      };
  //      CustomToast({ clientError });

  //      await leaveLive();
  //      setShowCreditsPopup(true);
  //    }
  //  };

  //  handleStop();
  //}, [creditsAvailable, showLiveViewer]);

  /* ===================== HANDLE TAB CLOSE ===================== */
  useEffect(() => {
    return () => {
      if (sessionIdRef.current) {
        SignalRService.leaveLiveChat(sessionIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleUnload = () => {
      dispatch(setPostPopupExpanded(false));
      dispatch(setViewLiveMinimized(false));

      if (sessionIdRef.current) {
        AV.Media.closeLiveSession({
          target: AV.AVEntity.getFk(entity),
          minutes: accumulatedMinutes.current ?? 0,
          mode: "Watch",
          channelId: showLiveViewer?.channelId ?? "",
          resourceId: showLiveViewer?.resourceId ?? "",
          sid: showLiveViewer?.sid ?? "",
        });

        SignalRService.leaveLiveChat(sessionIdRef.current);
      }
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  const startTimeRef = useRef<number>(0);
  const [liveAgo, setLiveAgo] = useState("");
  const [liveDuration, setLiveDuration] = useState("00:00:00");

  // ===================== INITIALIZE START TIME =====================
  useEffect(() => {
    if (!showLiveViewer?.date) return;

    const dateObj = new Date(showLiveViewer.date);
    startTimeRef.current = dateObj.getTime();
  }, [showLiveViewer?.date]);

  // ===================== LIVE DURATION TIMER =====================
  useEffect(() => {
    if (!startTimeRef.current) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const diff = now - startTimeRef.current;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      const formatted =
        String(hours).padStart(2, "0") +
        ":" +
        String(minutes).padStart(2, "0") +
        ":" +
        String(seconds).padStart(2, "0");

      setLiveDuration(formatted);
    }, 1000);

    return () => clearInterval(interval);
  }, [showLiveViewer?.date]);

  // ===================== LIVE AGO =====================
  useEffect(() => {
    if (!startTimeRef.current) return;

    const updateTime = () => {
      const now = Date.now();
      const diff = now - startTimeRef.current;

      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      let text = "";

      if (minutes < 1) {
        text = Loc.currentLang.convo.justNow;
      } else if (minutes === 1) {
        text = `1 ${Loc.currentLang.convo.minuteAgo}`;
      } else if (minutes < 60) {
        text = `${minutes} ${Loc.currentLang.convo.minutesAgo}`;
      } else if (hours === 1) {
        text = `1 ${Loc.currentLang.convo.hourAgo}`;
      } else if (hours < 24) {
        text = `${hours} ${Loc.currentLang.convo.hoursAgo}`;
      } else if (days === 1) {
        text = `1 ${Loc.currentLang.convo.daysAgo}`;
      } else {
        text = `${days} ${Loc.currentLang.convo.daysAgo}`;
      }

      setLiveAgo(text);
    };

    updateTime(); // initial

    const interval = setInterval(updateTime, 60000); // update every 1 min

    return () => clearInterval(interval);
  }, [showLiveViewer?.date]);

  //#endregion Livestream Section

  //#region SignalR(Live Chat) Section
  /* ===================== STATE ===================== */
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");

  const [joinMessages, setJoinMessages] = useState<
    { id: number; name: string }[]
  >([]);
  const [joinUsers, setJoinUsers] = useState<AV.ILink[]>([]);
  const [showParticipants, setShowParticipants] = useState(false);

  /* ================= REACTIONS ================= */
  const [reactions, setReactions] = useState<any[]>([]);
  const [showMoreReactions, setShowMoreReactions] = useState(false);
  const [reactionCounts, setReactionCounts] = useState({});
  const impressionTypes = useMemo(
    () => [
      { type: "Like", icon: settings.like, alt: "like" },
      { type: "Unlike", icon: settings.unlike, alt: "unLike" },
      { type: "heart", icon: settings.heart, alt: "heart" },
      { type: "Wow", icon: settings.wow, alt: "wow" },
      { type: "Lol", icon: settings.lol, alt: "lol" },
      { type: "Cheer", icon: settings.cheer, alt: "fan" },
      { type: "Question", icon: settings.question, alt: "Question" },
      { type: "broken", icon: settings.broken, alt: "broken" },
      { type: "Sad", icon: settings.sad, alt: "sads" },
      { type: "Mad", icon: settings.mad, alt: "mad" },
    ],
    [settings],
  );

  const menuRef = useRef<any>(null); // Menu reference (for click outside)
  useOutsideAlerter(menuRef, () => setShowMoreReactions(false)); // Close menu when clicking outside

  const participantsRef = useRef(null);
  useOutsideAlerter(participantsRef, () => setShowParticipants(false));

  const scrollRef = useRef<any>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /* ================= ADD REACTION ================= */
  const addReaction = (emoji: string, user: string) => {
    const id = Date.now();

    const translateY = new Animated.Value(120);
    const translateX = new Animated.Value(0);
    const opacity = new Animated.Value(1);
    const scale = new Animated.Value(0.6);

    // CURVE PATH CONTROL
    const curve = new Animated.Value(0);

    const newReaction = {
      id,
      emoji,
      user,
      translateY,
      translateX,
      opacity,
      scale,
      curve,
      rightOffset: Math.random() * 20 + 5,
    };

    setReactions((prev) => [...prev, newReaction]);

    /* ================= ANIMATION ================= */
    Animated.parallel([
      // FLOAT UP
      Animated.timing(translateY, {
        toValue: -window.innerHeight * 0.8,
        duration: 4000,
        useNativeDriver: true,
      }),

      // CURVE CONTROL (important)
      Animated.timing(curve, {
        toValue: 1,
        duration: 4000,
        useNativeDriver: true,
      }),

      // SCALE POP
      Animated.sequence([
        Animated.timing(scale, {
          toValue: 1.4,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(scale, {
          toValue: 1,
          duration: 3700,
          useNativeDriver: true,
        }),
      ]),

      // FADE
      Animated.sequence([
        Animated.delay(2500),
        Animated.timing(opacity, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: true,
        }),
      ]),
    ]).start(() => {
      setReactions((prev) => prev.filter((r) => r.id !== id));
    });
  };

  /* ===================== SIGNALR HOOK ===================== */
  useLiveSignalR({
    channelId: liveStream?.channelId || showLiveViewer?.channelId,
    setMessages,
    setJoinMessages,
    setJoinUsers,
    setReactionCounts,
    addReaction,
    formatText,
    Loc,
    leaveLive,
    userId: currentUser?.id,
    hostId: liveStream?.host?.id,
  });

  /* ===================== SEND MESSAGE ===================== */
  const sendMessage = async () => {
    if (!input.trim()) return;

    const channelId = liveStream?.channelId || showLiveViewer?.channelId;
    if (!channelId) return;

    const msg = {
      id: Date.now(),
      text: input,
      user: AV.AVEntity.getLink(currentUser),
    };

    await SignalRService.sendLiveMessage(channelId, msg);

    setInput("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  /* ===================== SEND REACTION ===================== */
  const sendReaction = async (emoji: string) => {
    const channelId = liveStream?.channelId || showLiveViewer?.channelId;
    if (!channelId) return;

    await SignalRService.sendLiveReaction(channelId, emoji);
  };

  /* ===================== AUTO SCROLL ===================== */
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  /* ===================== AUTO REMOVE JOIN MSG ===================== */
  useEffect(() => {
    if (!joinMessages.length) return;

    const timer = setTimeout(() => {
      setJoinMessages((prev) => prev.slice(1));
    }, 3000);

    return () => clearTimeout(timer);
  }, [joinMessages, joinUsers]);

  //#endregion SignalR(Live Chat) Section

  //#region Rewind(Livestream) Section

  const [isCurrentLive, setIsCurrentLive] = useState(true);
  const [showDVRSlider, setShowDVRSlider] = useState(true);

  const recordingVideoRef = useRef<HTMLVideoElement>(null);
  const agoraContainerRef = useRef<any>(null);

  const [recordingDuration, setRecordingDuration] = useState(0);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);

  const recordingMedia: AV.IMedia = {
    id: showLiveViewer?.sid ?? "",
    orgFile: `${showLiveViewer?.sid}_${showLiveViewer?.channelId}.m3u8`,
    storeId:
      currentUser?.region === "USD"
        ? "ST-USDWLS"
        : currentUser?.region === "UST"
          ? "ST-USTWLS"
          : "ST-USCWLS",
    type: "video/hls",

    size: 0,
    name: "",
    ratio: 0,
    previousID: "",
    linkURL: "",
  };

  const recordingUrl = AV.Media.getVideoStreamURL(recordingMedia);
  console.log(recordingUrl);

  const [playlistVersion, setPlaylistVersion] = useState(Date.now());

  useEffect(() => {
    if (!isCurrentLive || !showLiveViewer?.sid?.trim()) return;

    const interval = setInterval(() => {
      setPlaylistVersion(Date.now());
    }, 10000);

    return () => clearInterval(interval);
  }, [isCurrentLive]);

  const playbackUrl = `${recordingUrl}?v=${playlistVersion}`;
  console.log(playbackUrl);

  const formatTime = (seconds: number) => {
    const absSeconds = Math.abs(Math.floor(seconds));

    const hrs = Math.floor(absSeconds / 3600);
    const mins = Math.floor((absSeconds % 3600) / 60);
    const secs = absSeconds % 60;

    if (hrs > 0) {
      return `-${hrs}:${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }

    return `-${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (isCurrentLive) {
      setCurrentPlaybackTime(recordingDuration);
    }
  }, [recordingDuration, isCurrentLive]);

  //useEffect(() => {
  //  let timeout: any;
  //
  //  const showControls = () => {
  //    setShowDVRSlider(true);
  //
  //    clearTimeout(timeout);
  //
  //    timeout = setTimeout(() => {
  //      setShowDVRSlider(false);
  //    }, 3000);
  //  };
  //
  //  window.addEventListener("mousemove", showControls);
  //
  //  return () => {
  //    window.removeEventListener("mousemove", showControls);
  //    clearTimeout(timeout);
  //  };
  //}, []);

  //#endregion Rewind(Livestream) Section

  useEffect(() => {
    let isMounted = true;
    let initalCredits = 0;

    const loadPlan = async () => {
      let finalEntity = currentUser;
      //if (
      //  showLiveViewer?.owner?.type !== "Person" &&
      //  (showLiveViewer?.owner as AV.ILink)
      //) {
      //  let entityData =
      //    entityDataList?.find((x) => x?.id === showLiveViewer?.owner?.id) ??
      //    null;
      //
      //  if (!entityData) {
      //    entityData = await AV.AVEntity.get(showLiveViewer?.owner as AV.ILink);
      //    entityDataList?.push(entityData);
      //    dispatch(setEntitydata(entityDataList));
      //  }
      //
      //  const isOwner = !!entityData?.staff?.some(
      //    (staff) => staff.id === currentUser?.id,
      //  );
      //  setIsAdminView(
      //    showLiveViewer?.owner?.type === "Person" ? true : isOwner,
      //  );
      //
      //  finalEntity =
      //    showLiveViewer?.owner?.type === "Person"
      //      ? currentUser
      //      : isOwner
      //        ? entityData
      //        : currentUser;
      //}

      const handleCredits = (credits: number) => {
        initalCredits = credits;
        setCreditsAvailable(credits);
      };

      // Helper: Load Shared Plan Credits
      const loadSharedPlanCredits = async (entity: AV.IAVEntity) => {
        try {
          let shared =
            entityDataList?.find((x) => x?.id === entity.plan?.shared?.id) ??
            null;

          if (!shared) {
            shared = await AV.AVEntity.getSharedPlan(AV.AVEntity.getFk(entity));
            if (!isMounted) return;

            entityDataList?.push(shared);
            dispatch(setEntitydata(entityDataList));
          }

          setSharedPlan(shared);
          setFinalEntity(entity);
          handleCredits(shared?.plan?.credits ?? 0);
        } catch (e) {
          if (isMounted) console.error("Failed to load shared plan:", e);
        }
      };

      // NOT EVENT, check shared plan directly
      if (finalEntity?.type !== "Event" && finalEntity?.type !== "Game") {
        if (finalEntity.plan?.shared) {
          await loadSharedPlanCredits(finalEntity);
        } else {
          setFinalEntity(finalEntity);
          handleCredits(finalEntity?.plan?.credits ?? 0);
        }

        const isOwner = !!finalEntity?.staff?.some(
          (staff) => staff.id === currentUser?.id,
        );
        setIsAdminView(finalEntity?.type === "Person" ? true : isOwner);

        return;
      }

      //// EVENT, lookup parent entity
      //try {
      //  let parentEntity =
      //    entityDataList?.find((x) => x?.id === finalEntity?.parent?.id) ??
      //    null;
      //
      //  if (!parentEntity && finalEntity?.parent) {
      //    parentEntity = await AV.AVEntity.get(finalEntity?.parent);
      //    if (!isMounted) return;
      //
      //    entityDataList?.push(parentEntity);
      //    dispatch(setEntitydata(entityDataList));
      //  }
      //
      //  // Parent Shared Plan?
      //  if (parentEntity.plan?.shared) {
      //    await loadSharedPlanCredits(parentEntity);
      //  } else {
      //    setFinalEntity(parentEntity);
      //    setCreditsAvailable(parentEntity?.plan?.credits ?? 0);
      //    handleCredits(parentEntity?.plan?.credits ?? 0);
      //  }
      //  setEventParentEntity(parentEntity);
      //
      //  const isOwner = !!parentEntity?.staff?.some(
      //    (staff) => staff.id === currentUser?.id,
      //  );
      //  setIsAdminView(finalEntity?.type === "Person" ? true : isOwner);
      //} catch (error) {
      //  if (isMounted) console.error("Failed to load event credits:", error);
      //}
    };

    if (showLiveViewer) loadPlan();

    if (showLiveViewer && initalCredits < WATCH_START_CREDITS) {
      setShowMoreCreditPopup(true);
      return;
    }

    return () => {
      isMounted = false;
    };
  }, [showLiveViewer?.owner, showCreditsPopup /*, currentUser, entity*/]);

  useEffect(() => {
    dispatch(setPostPopupExpanded(true));

    return () => {
      dispatch(setPostPopupExpanded(false));
      //dispatch(setViewLiveMinimized(false));
    };
  }, [dispatch]);

  /* -------------------- UI -------------------- */

  const AVPreLivePopup = ({
    joinLive,
    isAPICall,
    videoUsers,
    toggleSpeaker,
    isSpeakerOn,
    creditsAvailable,
    setShowCreditsPopup,
  }) => {
    const user = videoUsers?.[0];

    return (
      <Box
        position="relative"
        w="95%"
        maxW="720px"
        maxH="90vh"
        borderRadius="8px"
        bg="white"
        p={{ base: "12px", md: "16px" }}
        shadow={8}
        alignSelf="center"
      >
        {/* ================= VIDEO SECTION ================= */}
        <Box
          position="relative"
          h={{ base: "260px", md: "40vh" }}
          minH="220px"
          maxH="450px"
          borderRadius="8px"
          overflow="hidden"
        >
          {/* CLOSE BUTTON */}
          <Pressable
            position="absolute"
            top="10px"
            right="10px"
            zIndex={100}
            w="36px"
            h="36px"
            bg="#FF3B30"
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
            onPress={leaveLive}
          >
            <AVCancel
              callback={leaveLive}
              color="white"
              size="24px"
              hoverColor="none"
            />
          </Pressable>

          {/* OVERLAY */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0,0,0,0.3)"
          />

          {/* CENTER CONTENT */}
          <VStack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            alignItems="center"
            justifyContent="center"
            space={3}
          >
            <Box alignItems="center" justifyContent="center">
              <PreLivestreamIcon height="64px" width="64px" />
            </Box>

            <Text fontSize="16px" fontWeight="600" color="white">
              {Loc.currentLang.liveStream.readyToWatchLive}
            </Text>

            <HStack space={3}>
              <Pressable
                onPress={toggleSpeaker}
                w="40px"
                h="40px"
                borderRadius="full"
                bg={isSpeakerOn ? "#32AE78" : "red.500"}
                alignItems="center"
                justifyContent="center"
              >
                {isSpeakerOn ? (
                  <FiVolume2 size={16} color="white" />
                ) : (
                  <FiVolumeX size={16} color="white" />
                )}
              </Pressable>
            </HStack>
          </VStack>
        </Box>

        {/* ================= CONTENT ================= */}
        <Box mt="10px" flex={1} overflow="auto">
          <VStack space={4} alignItems="center">
            {/* CREDITS */}
            <Text fontSize="16px" fontWeight="bold">
              {Loc.currentLang.myPlan.availableCredits}
            </Text>

            <DollarCoinsIcon height="80px" width="80px" />

            <Text fontSize="2xl" fontWeight="bold">
              {creditsAvailable}
            </Text>

            <Pressable onPress={() => setShowCreditsPopup(true)}>
              <Text fontSize="sm" color="#00A7BC" underline>
                {Loc.currentLang.manageConversation.buyMoreCredits}
              </Text>
            </Pressable>

            {/* INFO */}
            <HStack alignItems="center" space={2} px="10px">
              <Image source={inform} alt="info" size="16px" />

              {(() => {
                const minutes = Math.max(
                  1,
                  Math.floor(creditsAvailable / WATCH_COST_PER_MIN),
                );

                const message = formatText(
                  Loc.currentLang.liveStream.watchCreditsInfo,
                  [creditsAvailable, minutes, minutes !== 1 ? "s" : ""],
                );

                return (
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {message}
                  </Text>
                );
              })()}
            </HStack>

            {/* WATCH LIVE BUTTON */}
            <Button
              w={{ base: "70%", md: "30%" }}
              rounded="full"
              bg="AVColor.primary"
              _hover={{ bg: "AVColor.primary" }}
              _pressed={{ bg: "AVColor.primary" }}
              isLoading={isAPICall}
              isDisabled={isAPICall}
              onPress={joinLive}
            >
              {Loc.currentLang.liveStream.watchLive}
            </Button>
          </VStack>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {open && (
        <Box
          position={isMinimized ? "relative" : "fixed"}
          top={isMinimized ? undefined : 0}
          left={isMinimized ? undefined : 0}
          right={isMinimized ? undefined : 0}
          bottom={isMinimized ? undefined : 0}
          bg={isMinimized ? "transparent" : "rgba(0,0,0,0.4)"}
          alignItems="center"
          justifyContent="center"
          zIndex={999}
          px={isMinimized ? "0px" : "12px"}
          w="100%"
          h="100%"
        >
          {/* ================= PRE LIVE ================= */}
          {!isWatchLive ? (
            <AVPreLivePopup
              joinLive={joinLive}
              isAPICall={isAPICall}
              videoUsers={videoUsers}
              toggleSpeaker={toggleSpeaker}
              isSpeakerOn={isSpeakerOn}
              creditsAvailable={creditsAvailable}
              setShowCreditsPopup={setShowCreditsPopup}
            />
          ) : (
            <Box
              position={isMinimized ? "relative" : "relative"}
              width={isMinimized ? "100%" : "80vw"}
              height={isMinimized ? "100%" : "95vh"}
              borderRadius="12px"
              overflow="hidden"
              bg="black"
              zIndex={99999}
            >
              {/* ================= VIDEO ================= */}
              {videoUsers.length === 0 ? (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Text color="white">
                    {Loc.currentLang.liveStream.waitingForHost}
                  </Text>
                </Box>
              ) : (
                <>
                  {videoUsers.map((user) =>
                    !user.hasVideo ? (
                      <Box
                        key={user.uid}
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        justifyContent="center"
                        alignItems="center"
                        bg="black"
                      >
                        <EntityProfilePic
                          width={20}
                          height={20}
                          link={AV.AVEntity.getLink(
                            showLiveViewer?.host as unknown as AV.IAVEntity,
                          )}
                        />
                        <Text color="white" mt={2}>
                          {Loc.currentLang.liveStream.cameraOff}
                        </Text>
                      </Box>
                    ) : (
                      <Fragment key={user.uid}>
                        <Box
                          key={user.uid}
                          ref={(el) => {
                            agoraContainerRef.current = el;

                            if (el && user.videoTrack) {
                              user.videoTrack.play(el, {
                                fit: user.isPortrait ? "contain" : "cover",
                              });
                            }
                          }}
                          position="absolute"
                          top={0}
                          left={0}
                          w="100%"
                          h="100%"
                        />

                        {/* MIC MUTED */}
                        {user.hasAudio === false && (
                          <Box
                            position="absolute"
                            bottom={isMinimized ? "0px" : "100px"}
                            left={0}
                            right={0}
                            alignItems="center"
                          >
                            <Box
                              bg="#00000026"
                              px="14px"
                              py="6px"
                              borderRadius="20px"
                            >
                              <Text color="white" fontSize="13px">
                                <Text bold>
                                  {Loc.currentLang.liveStream.hostMutedMic}
                                </Text>
                              </Text>
                            </Box>
                          </Box>
                        )}
                      </Fragment>
                    ),
                  )}
                </>
              )}

              {showLiveViewer?.sid?.trim() && (
                <video
                  ref={recordingVideoRef}
                  src={playbackUrl}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "none",
                    background: "black",
                  }}
                  playsInline
                  preload="auto"
                  controls={false}
                  //onLoadedMetadata={(e) => {
                  //  setRecordingDuration(
                  //    (e.target as HTMLVideoElement).duration,
                  //  );
                  //}}
                  onLoadedMetadata={(e) => {
                    const duration = (e.target as HTMLVideoElement).duration;

                    setRecordingDuration(duration);
                    setCurrentPlaybackTime(duration); // Slider starts at end

                    if (isCurrentLive) {
                      setCurrentPlaybackTime(duration);
                    }
                  }}
                  //onTimeUpdate={(e) => {
                  //  setCurrentPlaybackTime(
                  //    (e.target as HTMLVideoElement).currentTime,
                  //  );
                  //}}
                  onTimeUpdate={(e) => {
                    const video = e.target as HTMLVideoElement;

                    const current = video.currentTime;
                    const remaining = recordingDuration - current;

                    setCurrentPlaybackTime(current);

                    // Auto Go Live
                    if (!isCurrentLive && remaining <= 3) {
                      setIsCurrentLive(true);

                      const agoraVideo = agoraContainerRef.current;
                      const recordingVideo = recordingVideoRef.current;

                      if (recordingVideo) {
                        recordingVideo.pause();
                        recordingVideo.style.display = "none";
                      }

                      if (agoraVideo) {
                        (agoraVideo as HTMLElement).style.display = "block";
                      }

                      //setCurrentPlaybackTime(recordingDuration);
                    }
                  }}
                />
              )}

              {showLiveViewer?.sid?.trim() && showDVRSlider && !isMinimized && (
                <Box
                  position="absolute"
                  bottom="80px"
                  left="20px"
                  right="20px"
                  zIndex={999}
                >
                  <Box
                    bg="rgba(0,0,0,0.55)"
                    borderRadius="8px"
                    px="12px"
                    py="10px"
                  >
                    <HStack
                      justifyContent="space-between"
                      alignItems="center"
                      mb="8px"
                    >
                      <Text color="white" fontSize="12px">
                        {!isCurrentLive
                          ? formatTime(recordingDuration - currentPlaybackTime)
                          : ""}
                      </Text>

                      {!isCurrentLive && (
                        <Pressable
                          onPress={() => {
                            setIsCurrentLive(true);
                            //setCurrentPlaybackTime(recordingDuration);

                            const agoraVideo = agoraContainerRef.current;
                            const recordingVideo = recordingVideoRef.current;

                            if (recordingVideo) {
                              recordingVideo.pause();
                              recordingVideo.style.display = "none";
                            }

                            if (agoraVideo) {
                              (agoraVideo as HTMLElement).style.display =
                                "block";
                            }
                          }}
                        >
                          <HStack alignItems="center" space={1}>
                            <Box
                              w="8px"
                              h="8px"
                              borderRadius="full"
                              bg="red.500"
                            />

                            <Text
                              color="white"
                              fontWeight="bold"
                              fontSize="12px"
                            >
                              {Loc.currentLang.liveStream.goLive}
                            </Text>
                          </HStack>
                        </Pressable>
                      )}
                    </HStack>

                    <input
                      type="range"
                      min={0}
                      max={recordingDuration}
                      value={
                        isCurrentLive ? recordingDuration : currentPlaybackTime
                      }
                      onMouseDown={() => {
                        //if (recordingDuration === currentPlaybackTime)
                        //  return;

                        setIsCurrentLive(false);

                        const agoraVideo = agoraContainerRef.current;
                        const recordingVideo = recordingVideoRef.current;

                        if (agoraVideo)
                          (agoraVideo as HTMLElement).style.display = "none";

                        if (recordingVideo) {
                          recordingVideo.style.display = "block";

                          recordingVideo
                            .play()
                            .then(() => {
                              console.log("Recording started");
                            })
                            .catch((err) => {
                              console.log("Recording play error", err);
                            });

                          // KEEP CURRENT POSITION
                          //recordingVideo.currentTime = currentPlaybackTime;
                          //recordingVideo.play().catch(() => {});

                          console.log(
                            "Duration:",
                            recordingDuration,
                            "Current:",
                            currentPlaybackTime,
                          );
                        }
                      }}
                      onChange={(e) => {
                        const value = Number(e.target.value);

                        setCurrentPlaybackTime(value);

                        if (recordingVideoRef.current) {
                          recordingVideoRef.current.currentTime = value;
                        }

                        const remaining = recordingDuration - value;

                        if (remaining <= 3) {
                          setIsCurrentLive(true);

                          const agoraVideo = agoraContainerRef.current;
                          const recordingVideo = recordingVideoRef.current;

                          if (recordingVideo) {
                            recordingVideo.pause();
                            recordingVideo.style.display = "none";
                          }

                          if (agoraVideo)
                            (agoraVideo as HTMLElement).style.display = "block";

                          return;
                        }

                        setIsCurrentLive(false);
                      }}
                      style={{
                        width: "100%",
                        height: "4px",
                        cursor: "pointer",
                        accentColor: "#FFFFFF",
                      }}
                    />
                  </Box>
                </Box>
              )}

              {!showOnlyVideo && !isMinimized && (
                <>
                  {/* ================= TOP LEFT ================= */}
                  <HStack
                    position="absolute"
                    top="16px"
                    left="16px"
                    alignItems="center"
                    space={2}
                  >
                    {/* PROFILE + NAME BLOCK */}
                    <HStack
                      alignItems="center"
                      space={2}
                      bg="#00000026"
                      px="10px"
                      py="6px"
                      borderRadius="full"
                    >
                      <EntityProfilePic
                        height={10}
                        width={10}
                        link={AV.AVEntity.getLink(
                          showLiveViewer?.host ?? currentUser,
                        )}
                      />

                      <VStack space={0}>
                        <Text color="white" fontSize="14px" fontWeight="bold">
                          {showLiveViewer?.host?.title ?? currentUser?.name}
                        </Text>
                        <Text color="white" fontSize="12px" opacity={0.7}>
                          {liveAgo}
                        </Text>
                      </VStack>
                    </HStack>

                    {/* VIEW COUNT PILL */}
                    <Pressable
                      onPress={() => setShowParticipants((prev) => !prev)}
                    >
                      <HStack
                        alignItems="center"
                        bg="#353130"
                        px="6px"
                        py="4px"
                        borderRadius="full"
                        space={2}
                      >
                        <FiUsers size={14} color="#FFFFFF" />

                        <Text color="white" fontSize="14px" fontWeight="bold">
                          {joinUsers?.length || 1}
                        </Text>
                      </HStack>
                    </Pressable>

                    {/* PARTICIPANTS POPUP */}
                    {showParticipants && (
                      <Box
                        ref={participantsRef}
                        position="absolute"
                        top="60px"
                        left="20px"
                        bg="white"
                        borderRadius="12px"
                        shadow={8}
                        width="280px"
                        maxH="350px"
                        zIndex={99999}
                        overflow="hidden"
                      >
                        <ScrollView
                          maxH="300px"
                          showsVerticalScrollIndicator={false}
                        >
                          {joinUsers?.length > 0 ? (
                            joinUsers.map((item, index) => (
                              <HStack
                                key={item?.id || index}
                                alignItems="center"
                                space={3}
                                px="12px"
                                py="10px"
                                borderBottomWidth={
                                  index !== joinUsers.length - 1 ? 1 : 0
                                }
                                borderBottomColor="#F1F1F1"
                              >
                                <EntityProfilePic
                                  width={8}
                                  height={8}
                                  link={item}
                                />

                                <VStack flex={1}>
                                  <Text fontSize="14px" fontWeight="600">
                                    {item?.title}{" "}
                                    {showLiveViewer?.host?.id === item?.id && (
                                      <>({Loc.currentLang.liveStream.host}) </>
                                    )}
                                    {currentUser?.id === item?.id && (
                                      <>({Loc.currentLang.menu.you})</>
                                    )}
                                  </Text>
                                </VStack>
                              </HStack>
                            ))
                          ) : (
                            <Text p="15px" textAlign="center" color="gray.500">
                              {Loc.currentLang.liveStream.noParticipants}
                            </Text>
                          )}
                        </ScrollView>
                      </Box>
                    )}
                  </HStack>

                  {/* ================= TOP CENTER ================= */}
                  {videoUsers.length > 0 && (
                    <Box
                      position="absolute"
                      top="35px"
                      alignSelf="center"
                      bg="#00000026"
                      px="12px"
                      py="6px"
                      borderRadius="full"
                    >
                      <HStack alignItems="center" space={2}>
                        <HStack
                          alignItems="center"
                          space={1}
                          bg="#F44336"
                          px="10px"
                          py="4px"
                          borderRadius="full"
                        >
                          <LiveIcon width="15px" height="15px" />
                          <Text color="white" fontSize="14px">
                            {Loc.currentLang.liveStream.live}
                          </Text>
                        </HStack>

                        <Box w="1.5px" h="28px" bg="rgba(255,255,255,0.3)" />

                        <Text color="white">{liveDuration}</Text>
                      </HStack>
                    </Box>
                  )}

                  {/* CENTER JOIN/LEFT MESSAGES */}
                  <Box
                    position="absolute"
                    top="100px"
                    left={0}
                    right={0}
                    alignItems="center"
                  >
                    {joinMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className="fadeSlide"
                        style={{ marginBottom: "6px" }}
                      >
                        <Box
                          bg="#00000026"
                          px="14px"
                          py="6px"
                          borderRadius="20px"
                        >
                          <Text color="white" fontSize="13px">
                            <Text bold>{msg.name}</Text>
                          </Text>
                        </Box>
                      </div>
                    ))}
                  </Box>
                </>
              )}

              {/* ================= TOP RIGHT(CLOSE & REACTIONS) ================= */}
              {!isMinimized && (
                <Pressable
                  position="absolute"
                  top="30px"
                  right="120px"
                  zIndex={100}
                  w="36px"
                  h="36px"
                  bg="#32AE78"
                  borderRadius="full"
                  alignItems="center"
                  justifyContent="center"
                  onPress={() => {
                    dispatch(setViewLiveMinimized(true));
                    dispatch(setPostPopupExpanded(false));
                  }}
                >
                  <LiveMinIcon />
                </Pressable>
              )}

              {isMinimized && (
                <Pressable
                  position="absolute"
                  top={isMinimized ? "12px" : "30px"}
                  left="20px"
                  zIndex={100}
                  w="36px"
                  h="36px"
                  bg="#32AE78"
                  borderRadius="full"
                  alignItems="center"
                  justifyContent="center"
                  onPress={() => {
                    dispatch(setViewLiveMinimized(false));
                    dispatch(setPostPopupExpanded(true));
                  }}
                >
                  <LiveMaxIcon />
                </Pressable>
              )}

              <Pressable
                position="absolute"
                top={isMinimized ? "12px" : "30px"}
                right="20px"
                zIndex={100}
                w="36px"
                h="36px"
                bg="#FF3B30"
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                onPress={() => setIsConfirm(true)}
              >
                <AVCancel
                  callback={() => setIsConfirm(true)}
                  color="white"
                  size="24px"
                  hoverColor="none"
                />
              </Pressable>

              {!isMinimized && (
                <Pressable
                  position="absolute"
                  top="30px"
                  right="70px"
                  zIndex={100}
                  w="36px"
                  h="36px"
                  bg="#32AE78"
                  borderRadius="full"
                  alignItems="center"
                  justifyContent="center"
                  onPress={() => setShowOnlyVideo((prev) => !prev)}
                >
                  {showOnlyVideo ? (
                    <FiEyeOff size={16} color="white" />
                  ) : (
                    <FiEye size={16} color="white" />
                  )}
                </Pressable>
              )}

              {!showOnlyVideo && !isMinimized && (
                <>
                  {Object.entries(reactionCounts).length > 0 && (
                    <Box position="absolute" top="100px" right="20px">
                      <HStack
                        bg="#00000026"
                        px="16px"
                        py="8px"
                        borderRadius="full"
                        space={4}
                      >
                        {Object.entries(reactionCounts).map(
                          ([emoji, count]) => {
                            const reaction = impressionTypes.find(
                              (type) => type.type === emoji,
                            );

                            return (
                              <HStack key={emoji} alignItems="center" space={1}>
                                <Image
                                  source={
                                    reaction?.icon
                                      ? { uri: reaction.icon }
                                      : undefined
                                  }
                                  alt={reaction?.alt}
                                  size="18px"
                                />

                                <Text
                                  color="white"
                                  fontSize="14px"
                                  fontWeight="500"
                                >
                                  {count}
                                </Text>
                              </HStack>
                            );
                          },
                        )}
                      </HStack>
                    </Box>
                  )}

                  {/* ================= CHAT CONTAINER ================= */}
                  <div
                    ref={chatRef}
                    style={{
                      position: "absolute",
                      top: 80,
                      bottom: 75,
                      left: 12,
                      width: "80%",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      paddingRight: 4,
                      scrollbarWidth: "none",
                    }}
                  >
                    {/* PUSH CONTENT TO BOTTOM */}
                    <div style={{ flex: 1 }} />

                    <VStack space={4}>
                      {messages.map((msg) => {
                        const isMe = currentUser?.id === msg?.user?.id;

                        const userEntity = isMe
                          ? currentUser
                          : entityDataList?.find(
                              (x) => x?.id === msg?.user?.id,
                            ) || msg?.user;

                        const userLink = AV.AVEntity.getLink(userEntity);

                        return (
                          <HStack
                            key={msg.id}
                            space={2}
                            alignItems="flex-start"
                          >
                            <EntityProfilePic
                              width={10}
                              height={10}
                              link={userLink}
                            />

                            <VStack maxW="45%">
                              <Text
                                fontSize="14px"
                                fontWeight="600"
                                color="#000000D9"
                              >
                                {msg.user.title}
                              </Text>

                              <div
                                style={{
                                  backdropFilter: "blur(10px)",
                                  borderRadius: "14px",
                                  marginTop: "4px",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                <Box
                                  px="14px"
                                  py="10px"
                                  borderRadius="14px"
                                  bg="#98929299"
                                >
                                  <Text color="white" fontSize="14px">
                                    {msg.text}
                                  </Text>
                                </Box>
                              </div>
                            </VStack>
                          </HStack>
                        );
                      })}
                    </VStack>
                  </div>

                  {/* ================= RIGHT CONTROLS ================= */}
                  <HStack
                    position="absolute"
                    right="16px"
                    bottom={showLiveViewer?.sid?.trim() ? "140px" : "90px"}
                    space={3}
                  >
                    <Pressable
                      onPress={toggleSpeaker}
                      bg={isSpeakerOn ? "#32AE78" : "red.500"}
                      p="10px"
                      borderRadius="full"
                    >
                      {isSpeakerOn ? (
                        <FiVolume2 size={16} color="white" />
                      ) : (
                        <FiVolumeX size={16} color="white" />
                      )}
                    </Pressable>
                  </HStack>

                  {/* ================= FLOATING REACTIONS ================= */}
                  <Box
                    position="absolute"
                    bottom="70px"
                    right="70px"
                    w="160px"
                    h="80%"
                    pointerEvents="none"
                    overflow="hidden"
                    zIndex={60}
                  >
                    {reactions.map((r) => {
                      const reaction = impressionTypes.find(
                        (type) => type.type === r.emoji,
                      );

                      return (
                        <Animated.View
                          key={r.id}
                          style={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            transform: [
                              { translateY: r.translateY },
                              {
                                translateX: r.curve.interpolate({
                                  inputRange: [0, 0.5, 1],
                                  outputRange: [0, -30, 30],
                                }),
                              },
                              { scale: r.scale },
                            ],
                            opacity: r.opacity,
                            alignItems: "center",
                          }}
                        >
                          {reaction && (
                            <Image
                              source={
                                typeof reaction.icon === "string"
                                  ? { uri: reaction.icon }
                                  : reaction.icon
                              }
                              alt={reaction.alt}
                              size="24px"
                            />
                          )}

                          {/* USER NAME */}
                          {/*<Box*/}
                          {/*  mt="2px"*/}
                          {/*  px="6px"*/}
                          {/*  py="2px"*/}
                          {/*  borderRadius="6px"*/}
                          {/*  bg="rgba(0,0,0,0.6)"*/}
                          {/*>*/}
                          {/*  <Text color="white" fontSize="10px">*/}
                          {/*    {r.user}*/}
                          {/*  </Text>*/}
                          {/*</Box>*/}
                        </Animated.View>
                      );
                    })}
                  </Box>

                  {/* ================= BOTTOM BAR ================= */}
                  <Box
                    position="absolute"
                    bottom={0}
                    w="100%"
                    bg="#FFFFFF"
                    px="12px"
                    py="14px"
                  >
                    <HStack alignItems="center" space={2}>
                      {/* REACTIONS */}
                      <HStack space={2}>
                        {[
                          { type: "Like", icon: settings.like, alt: "like" },
                          { type: "heart", icon: settings.heart, alt: "heart" },
                          { type: "Cheer", icon: settings.cheer, alt: "cheer" },
                        ].map((reaction) => (
                          <Pressable
                            key={reaction.type}
                            onPress={() => {
                              sendReaction(reaction.type);
                              setShowMoreReactions(false);
                            }}
                          >
                            <HStack>
                              <Tooltip
                                title={
                                  Loc.currentLang.impression[
                                    reaction.type.toLowerCase()
                                  ]
                                }
                                enterDelay={100}
                                leaveDelay={0}
                                arrow
                                placement="top"
                              >
                                <span style={{ display: "inline-block" }}>
                                  {" "}
                                  <Image
                                    source={{ uri: reaction.icon }}
                                    alt={reaction.alt}
                                    size="26px"
                                  />
                                </span>
                              </Tooltip>
                            </HStack>
                          </Pressable>
                        ))}

                        <Button
                          width="36px"
                          height="36px"
                          bg="#EEEEEEEE"
                          _hover={{ bgColor: "gray.50" }}
                          rounded="full"
                          shadow={1}
                          p={0}
                          onPress={() =>
                            setShowMoreReactions(!showMoreReactions)
                          }
                        >
                          <AddIcon size="20px" color="#000000CC" />
                        </Button>
                      </HStack>

                      {/* INPUT */}
                      <Input
                        ref={inputRef}
                        flex={1}
                        h="44px"
                        bg="white"
                        borderRadius="full"
                        px="16px"
                        fontSize="15px"
                        placeholder={
                          Loc.currentLang.placeholderText.messagePlaceholder
                        }
                        borderWidth={1}
                        borderColor="#D9D9D9"
                        _focus={{
                          bg: "white",
                          borderWidth: 1,
                        }}
                        _hover={{
                          bg: "white",
                          borderWidth: 1,
                        }}
                        value={input}
                        onChangeText={(text) => setInput(text)}
                        onSubmitEditing={() => {
                          if (input.trim()) {
                            sendMessage();
                            setTimeout(() => inputRef.current?.focus(), 0);
                          }
                        }}
                      />

                      {/* SEND */}
                      <Pressable
                        w="40px"
                        h="40px"
                        bg="#32AE78"
                        borderRadius="full"
                        alignItems="center"
                        justifyContent="center"
                        onPress={sendMessage}
                      >
                        <PaperPlaneIcon size="18px" color="#fff" />
                      </Pressable>
                    </HStack>

                    {/* MORE REACTIONS */}
                    {showMoreReactions && (
                      <HStack
                        ref={menuRef}
                        position="absolute"
                        bottom="80px"
                        left="12px"
                        bg="#FFFFFF"
                        px="12px"
                        py="8px"
                        borderRadius="24px"
                        shadow={4}
                      >
                        {impressionTypes.map((reaction) => (
                          <Pressable
                            key={reaction.type}
                            onPress={() => {
                              sendReaction(reaction.type);
                              setShowMoreReactions(false);
                            }}
                          >
                            <HStack>
                              <Tooltip
                                title={
                                  Loc.currentLang.impression[
                                    reaction.type.toLowerCase()
                                  ]
                                }
                                enterDelay={100}
                                leaveDelay={0}
                                arrow
                                placement="top"
                              >
                                <span style={{ display: "inline-block" }}>
                                  {" "}
                                  <Image
                                    source={{ uri: reaction.icon }}
                                    alt={reaction.alt}
                                    size="26px"
                                  />
                                </span>
                              </Tooltip>
                            </HStack>
                          </Pressable>
                        ))}
                      </HStack>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      )}

      {showMoreCreditPopup && (
        <Box {...popupOverlay}>
          <Box {...popupBox}>
            <Box position="absolute" top="10px" right="10px">
              <AVCancel
                callback={() => setShowMoreCreditPopup(false)}
                color={undefined}
                size={undefined}
              />
            </Box>

            <Text fontSize="18px" fontWeight="600" mb={2}>
              {Loc.currentLang.liveStream.notEnoughCredits}
            </Text>

            <Text fontSize="14px" color="rgba(0,0,0,0.7)" mb={5}>
              {Loc.currentLang.liveStream.watchMinCredits}
            </Text>

            <HStack justifyContent="flex-end">
              <Button
                bg="AVColor.primary"
                borderRadius="full"
                px={5}
                _hover={{ bg: "AVColor.primary" }}
                _pressed={{ bg: "AVColor.primary" }}
                onPress={() => {
                  setShowMoreCreditPopup(false);
                  setShowCreditsPopup(true);
                }}
              >
                <Text color="white">
                  {Loc.currentLang.manageConversation.buyMoreCredits}
                </Text>
              </Button>
            </HStack>
          </Box>
        </Box>
      )}

      {showLowCreditPopup &&
        (() => {
          const minutes = Math.max(
            1,
            Math.floor(creditsAvailable / WATCH_COST_PER_MIN),
          );

          const message = formatText(
            Loc.currentLang.liveStream.watchLowCredits,
            [minutes, minutes !== 1 ? "s" : ""],
          );

          return (
            <Box {...popupOverlay}>
              <Box {...popupBox}>
                <Text fontSize="18px" fontWeight="600" mb={2}>
                  {Loc.currentLang.liveStream.lowCredits}
                </Text>

                <Text fontSize="14px" color="rgba(0,0,0,0.7)" mb={5}>
                  {message}
                </Text>

                <HStack justifyContent="flex-end" space={3}>
                  <Button
                    variant="ghost"
                    onPress={() => setShowLowCreditPopup(false)}
                  >
                    <Text>{Loc.currentLang.liveStream.continue}</Text>
                  </Button>

                  {/*{isAdminView && (*/}
                  <Button
                    bg="AVColor.primary"
                    borderRadius="full"
                    px={5}
                    _hover={{ bg: "AVColor.primary" }}
                    _pressed={{ bg: "AVColor.primary" }}
                    onPress={() => {
                      setShowLowCreditPopup(false);
                      setShowCreditsPopup(true);
                    }}
                  >
                    <Text color="white">
                      {Loc.currentLang.manageConversation.buyMoreCredits}
                    </Text>
                  </Button>
                  {/*)}*/}
                </HStack>
              </Box>
            </Box>
          );
        })()}

      {showCreditsPopup && (
        /*isAdminView &&*/ <Box
          position="fixed"
          bgColor="rgba(0, 0, 0, 0.25)"
          bottom={0}
          right={0}
          top="40px"
          left={0}
          zIndex={99999}
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflowY="auto"
          px="16px"
        >
          <PurchaseCreditsPopup
            //entity={
            //  showLiveViewer?.owner?.type === "Event" ||
            //  showLiveViewer?.owner?.type === "Game"
            //    ? eventParentEntity
            //    : showLiveViewer?.owner?.type === "Person"
            //      ? currentUser
            //      : finalEntity
            //}
            entity={finalEntity}
            setEntity={setEntity}
            sharedPlan={sharedPlan}
            setSharedPlan={setSharedPlan}
            showCreditsPopup={showCreditsPopup}
            setShowCreditsPopup={setShowCreditsPopup}
          />
        </Box>
      )}

      {isConfirm && (
        <AVConfirmationModal
          showconfirmationPopup={isConfirm}
          setshowconfirmationPopup={setIsConfirm}
          Confirmationtext={Loc.currentLang.liveStream.confirmWatchLive}
          type={"LeaveLive"}
          item={undefined}
          function={leaveLive}
          isLoader={isAPICall}
        />
      )}
    </>
  );
};

export default AVLiveViewPopup;
